import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, Row, Col, message, Spin, Alert, List, Tag } from 'antd';
import { RobotOutlined, SafetyOutlined, CopyOutlined } from '@ant-design/icons';
import { generateTitles, generateCopywriting, checkForbiddenWords } from '../services/api';

const { TextArea } = Input;
const { Option } = Select;

const ContentCenter = () => {
  const [titleForm] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatedTitles, setGeneratedTitles] = useState([]);
  const [generatedCopywriting, setGeneratedCopywriting] = useState('');
  const [forbiddenCheck, setForbiddenCheck] = useState(null);

  // 生成标题
  const handleGenerateTitles = async (values) => {
    setLoading(true);
    try {
      const response = await generateTitles({
        theme: values.theme,
        style: values.style,
        industry: values.industry,
        count: 10
      });

      if (response.success) {
        setGeneratedTitles(response.data.titles);
        message.success(`成功生成 ${response.data.titles.length} 个标题`);
      }
    } catch (error) {
      message.error(error.message || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  // 生成文案
  const handleGenerateCopywriting = async (values) => {
    setLoading(true);
    try {
      const response = await generateCopywriting({
        theme: values.theme,
        title: values.title,
        duration: values.duration || 30,
        style: values.copyStyle
      });

      if (response.success) {
        setGeneratedCopywriting(response.data.copywriting);
        
        if (!response.data.isSafe) {
          message.warning(`检测到 ${response.data.forbiddenWords.length} 个违禁词`);
        } else {
          message.success('文案生成成功');
        }
      }
    } catch (error) {
      message.error(error.message || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  // 检测违禁词
  const handleCheckForbidden = async () => {
    if (!generatedCopywriting) {
      message.warning('请先生成文案');
      return;
    }

    try {
      const response = await checkForbiddenWords({ text: generatedCopywriting });
      setForbiddenCheck(response.data);

      if (response.data.isSafe) {
        message.success('✅ 文案安全，无违禁词');
      } else {
        message.error(`❌ 检测到 ${response.data.forbiddenWords.length} 个违禁词`);
      }
    } catch (error) {
      message.error('检测失败');
    }
  };

  // 复制文案
  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCopywriting);
    message.success('已复制到剪贴板');
  };

  return (
    <div>
      <Card 
        title="智能内容创作中心"
        extra={<RobotOutlined />}
      >
        {/* 标题生成 */}
        <section style={{ marginBottom: 32 }}>
          <h3>🎯 智能标题生成</h3>
          <Form
            form={titleForm}
            onFinish={handleGenerateTitles}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="theme"
                  label="主题/关键词"
                  rules={[{ required: true, message: '请输入主题' }]}
                >
                  <Input placeholder="例如：美食教程、健身技巧" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="style"
                  label="标题风格"
                  initialValue="爆款引流"
                >
                  <Select>
                    <Option value="爆款引流">爆款引流</Option>
                    <Option value="简洁直白">简洁直白</Option>
                    <Option value="悬念吸引">悬念吸引</Option>
                    <Option value="干货科普">干货科普</Option>
                    <Option value="互动提问">互动提问</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="industry"
                  label="行业领域"
                  initialValue="通用"
                >
                  <Select>
                    <Option value="通用">通用</Option>
                    <Option value="美食">美食</Option>
                    <Option value="美妆">美妆</Option>
                    <Option value="知识">知识</Option>
                    <Option value="电商">电商</Option>
                    <Option value="生活">生活</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label=" ">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    block
                  >
                    生成标题
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {generatedTitles.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>生成结果：</h4>
              <List
                grid={{ gutter: 16, column: 2 }}
                dataSource={generatedTitles}
                renderItem={(item) => (
                  <List.Item>
                    <Card 
                      size="small" 
                      style={{ 
                        width: '100%',
                        borderLeft: item.isSafe ? '3px solid #52c41a' : '3px solid #ff4d4f'
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>{item.title}</div>
                      <div>
                        {item.isSafe ? (
                          <Tag color="success" icon={<SafetyOutlined />}>安全</Tag>
                        ) : (
                          <Tag color="error">
                            违禁词：{item.forbiddenWords.join(', ')}
                          </Tag>
                        )}
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          )}
        </section>

        {/* 文案生成 */}
        <section>
          <h3>📝 智能文案生成</h3>
          <Form
            form={copyForm}
            onFinish={handleGenerateCopywriting}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="theme"
                  label="主题"
                  rules={[{ required: true, message: '请输入主题' }]}
                >
                  <Input placeholder="视频主题" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="title"
                  label="标题（可选）"
                >
                  <Input placeholder="可使用上面生成的标题" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="duration"
                  label="视频时长 (秒)"
                  initialValue={30}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  name="copyStyle"
                  label="文案风格"
                  initialValue="口播"
                >
                  <Select>
                    <Option value="口播">口播</Option>
                    <Option value="字幕">字幕</Option>
                    <Option value="剧情">剧情</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={2}>
                <Form.Item label=" ">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    block
                  >
                    生成
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {generatedCopywriting && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4>生成结果：</h4>
                <div>
                  <Button 
                    icon={<SafetyOutlined />} 
                    onClick={handleCheckForbidden}
                    style={{ marginRight: 8 }}
                  >
                    检测违禁词
                  </Button>
                  <Button icon={<CopyOutlined />} onClick={handleCopy}>
                    复制文案
                  </Button>
                </div>
              </div>

              {forbiddenCheck && !forbiddenCheck.isSafe && (
                <Alert
                  message={`检测到违禁词：${forbiddenCheck.forbiddenWords.join(', ')}`}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <TextArea
                value={generatedCopywriting}
                readOnly
                rows={8}
                style={{ fontSize: 14, lineHeight: 1.8 }}
              />
            </div>
          )}
        </section>
      </Card>
    </div>
  );
};

export default ContentCenter;
